/**
 * Interlocking tile calculator (2' × 2' tiles, 4 sq ft each).
 * Reference logic: total area L×W (ft), tiles = area/4, borders = (L-4)+(W-4), corners = 4, centers = tiles - borders - 4.
 */
(function () {
  var SQ_FT_PER_TILE = 4;
  var M_TO_FT = 3.280839895;
  var SQ_M_TO_SQ_FT = 10.76391041671;

  function roundUpToEven(n) {
    if (!isFinite(n) || n <= 0) return NaN;
    var c = Math.ceil(n);
    return c % 2 === 0 ? c : c + 1;
  }

  function parseNum(v) {
    if (v === '' || v === null || v === undefined) return NaN;
    var n = parseFloat(String(v).replace(',', '.'));
    return n;
  }

  function formatInt(n) {
    if (!isFinite(n)) return '—';
    return String(Math.round(n));
  }

  function formatArea(n, decimals) {
    if (!isFinite(n)) return '—';
    var d = decimals === undefined ? 2 : decimals;
    var f = Math.pow(10, d);
    return String(Math.round(n * f) / f);
  }

  /**
   * Theme accordions set an explicit height then "auto"; inner height changes
   * (e.g. ft ↔ m labels, results) do not always reflow — nudge the open panel.
   */
  function refreshParentCollapsibleHeight(root) {
    if (!root) return;
    var panel = root.closest('.collapsible-content') || root.closest('.product-accordion__content');
    if (!panel) return;

    if (panel.classList.contains('collapsible-content')) {
      if (!panel.classList.contains('is-open')) return;
      var inner = panel.querySelector(':scope > .collapsible-content__inner');
      if (!inner) return;

      var h = Math.max(inner.offsetHeight, inner.scrollHeight);
      if (h < 1) return;

      panel.style.height = h + 'px';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          panel.style.height = 'auto';
        });
      });
      return;
    }

    var accordion = panel.closest('details.product-accordion');
    if (!accordion || !accordion.classList.contains('is-open')) return;

    var accordionInner = panel.querySelector('.product-accordion__content-inner');
    if (!accordionInner) return;

    var accordionHeight = Math.ceil(accordionInner.scrollHeight || accordionInner.offsetHeight);
    if (accordionHeight < 1) return;

    panel.style.height = accordionHeight + 'px';
  }

  function scheduleCollapsibleReflow(root) {
    if (!root) return;
    if (root._fcReflowRaf) cancelAnimationFrame(root._fcReflowRaf);
    root._fcReflowRaf = requestAnimationFrame(function () {
      root._fcReflowRaf = null;
      refreshParentCollapsibleHeight(root);
    });
  }

  function computeFromFeet(L, W) {
    L = roundUpToEven(L);
    W = roundUpToEven(W);
    if (!isFinite(L) || !isFinite(W)) return { error: 'invalid' };

    if (L < 4 || W < 4) {
      return { error: 'small', lengthFt: L, widthFt: W };
    }

    var totalSqFt = L * W;
    var totalTiles = totalSqFt / SQ_FT_PER_TILE;
    var borders = L - 4 + (W - 4);
    var corners = 4;
    var centers = totalTiles - borders - corners;

    return {
      lengthFt: L,
      widthFt: W,
      totalSqFt: totalSqFt,
      totalTiles: Math.round(totalTiles),
      borders: Math.round(borders),
      corners: Math.round(corners),
      centers: Math.max(0, Math.round(centers))
    };
  }

  function bind(root) {
    if (!root || root.getAttribute('data-flooring-bound') === 'true') return;
    root.setAttribute('data-flooring-bound', 'true');

    var FT_TO_M = 1 / M_TO_FT;
    var unit = null; // 'ft' | 'm' — null until first setUnit (avoids convert on init)
    var mode = 'dimensions'; // 'dimensions' | 'area'

    function fmtConverted(n, decimals) {
      var f = Math.pow(10, decimals);
      var x = Math.round(n * f) / f;
      return String(x);
    }

    var msgSmallRoom =
      root.getAttribute('data-fc-err-small') ||
      'After rounding to a 2 ft tile grid, each side should be at least 4 ft. Enter larger dimensions.';
    var gridSep = root.getAttribute('data-fc-grid-sep') || ' × ';
    var gridSuf = root.getAttribute('data-fc-grid-suf') || ' ft';

    var els = {
      unitFt: root.querySelector('[data-fc-unit="ft"]'),
      unitM: root.querySelector('[data-fc-unit="m"]'),
      modeDim: root.querySelector('[data-fc-mode="dimensions"]'),
      modeArea: root.querySelector('[data-fc-mode="area"]'),
      rowDim: root.querySelector('[data-fc-row="dimensions"]'),
      rowArea: root.querySelector('[data-fc-row="area"]'),
      len: root.querySelector('[data-fc-input="length"]'),
      wid: root.querySelector('[data-fc-input="width"]'),
      area: root.querySelector('[data-fc-input="area"]'),
      lblLen: root.querySelector('[data-fc-label-len]'),
      lblWid: root.querySelector('[data-fc-label-wid]'),
      lblArea: root.querySelector('[data-fc-label-area]'),
      outCovFt: root.querySelector('[data-fc-out="coverage-ft"]'),
      outCovM: root.querySelector('[data-fc-out="coverage-m"]'),
      outTiles: root.querySelector('[data-fc-out="tiles"]'),
      outCorners: root.querySelector('[data-fc-out="corners"]'),
      outBorders: root.querySelector('[data-fc-out="borders"]'),
      outCenters: root.querySelector('[data-fc-out="centers"]'),
      outGrid: root.querySelector('[data-fc-out="grid"]'),
      err: root.querySelector('[data-fc-error]'),
      reset: root.querySelector('[data-fc-reset]')
    };

    function setUnit(u) {
      var prev = unit;
      if (prev !== null && prev !== u) {
        if (mode === 'dimensions') {
          if (els.len && els.len.value !== '') {
            var lv = parseNum(els.len.value);
            if (isFinite(lv)) {
              if (prev === 'ft' && u === 'm') els.len.value = fmtConverted(lv * FT_TO_M, 4);
              if (prev === 'm' && u === 'ft') els.len.value = fmtConverted(lv * M_TO_FT, 4);
            }
          }
          if (els.wid && els.wid.value !== '') {
            var wv = parseNum(els.wid.value);
            if (isFinite(wv)) {
              if (prev === 'ft' && u === 'm') els.wid.value = fmtConverted(wv * FT_TO_M, 4);
              if (prev === 'm' && u === 'ft') els.wid.value = fmtConverted(wv * M_TO_FT, 4);
            }
          }
        } else if (els.area && els.area.value !== '') {
          var av = parseNum(els.area.value);
          if (isFinite(av)) {
            if (prev === 'ft' && u === 'm') els.area.value = fmtConverted(av * 0.09290304, 4);
            if (prev === 'm' && u === 'ft') els.area.value = fmtConverted(av * SQ_M_TO_SQ_FT, 2);
          }
        }
      }
      unit = u;
      if (els.unitFt && els.unitM) {
        els.unitFt.classList.toggle('is-selected', u === 'ft');
        els.unitM.classList.toggle('is-selected', u === 'm');
      }
      if (els.lblLen) els.lblLen.textContent = u === 'ft' ? 'ft' : 'm';
      if (els.lblWid) els.lblWid.textContent = u === 'ft' ? 'ft' : 'm';
      if (els.lblArea) els.lblArea.textContent = u === 'ft' ? 'sq ft' : 'sq m';
      update();
    }

    function setMode(m) {
      mode = m;
      if (els.modeDim) els.modeDim.checked = m === 'dimensions';
      if (els.modeArea) els.modeArea.checked = m === 'area';
      if (els.rowDim) els.rowDim.classList.toggle('flooring-calculator__hidden', m !== 'dimensions');
      if (els.rowArea) els.rowArea.classList.toggle('flooring-calculator__hidden', m !== 'area');
      update();
    }

    function dimensionsToFeet() {
      var L = parseNum(els.len ? els.len.value : '');
      var W = parseNum(els.wid ? els.wid.value : '');
      if (!isFinite(L) || !isFinite(W) || L <= 0 || W <= 0) return null;
      if (unit === 'm') {
        L = L * M_TO_FT;
        W = W * M_TO_FT;
      }
      return { L: L, W: W };
    }

    function areaToFeetSquare() {
      var A = parseNum(els.area ? els.area.value : '');
      if (!isFinite(A) || A <= 0) return null;
      var sqFt;
      if (unit === 'ft') {
        sqFt = A;
      } else {
        sqFt = A * SQ_M_TO_SQ_FT;
      }
      var side = Math.sqrt(sqFt);
      return { L: side, W: side };
    }

    function update() {
      try {
        root.classList.toggle('flooring-calculator--has-results', false);
        if (els.err) {
          els.err.textContent = '';
          els.err.hidden = true;
        }

        var ft = mode === 'dimensions' ? dimensionsToFeet() : areaToFeetSquare();
        if (!ft) {
          if (els.outCovFt) els.outCovFt.textContent = '—';
          if (els.outCovM) els.outCovM.textContent = '—';
          if (els.outTiles) els.outTiles.textContent = '—';
          if (els.outCorners) els.outCorners.textContent = '—';
          if (els.outBorders) els.outBorders.textContent = '—';
          if (els.outCenters) els.outCenters.textContent = '—';
          if (els.outGrid) els.outGrid.textContent = '—';
          return;
        }

        var res = computeFromFeet(ft.L, ft.W);
        if (res.error === 'small') {
          if (els.err) {
            els.err.textContent = msgSmallRoom;
            els.err.hidden = false;
          }
          if (els.outCovFt) els.outCovFt.textContent = '—';
          if (els.outCovM) els.outCovM.textContent = '—';
          if (els.outTiles) els.outTiles.textContent = '—';
          if (els.outCorners) els.outCorners.textContent = '—';
          if (els.outBorders) els.outBorders.textContent = '—';
          if (els.outCenters) els.outCenters.textContent = '—';
          if (els.outGrid) els.outGrid.textContent = '—';
          return;
        }
        if (res.error) return;

        root.classList.add('flooring-calculator--has-results');

        var sqM = res.totalSqFt * 0.09290304;
        if (els.outCovFt) els.outCovFt.textContent = formatArea(res.totalSqFt, 1);
        if (els.outCovM) els.outCovM.textContent = formatArea(sqM, 2);
        if (els.outTiles) els.outTiles.textContent = formatInt(res.totalTiles);
        if (els.outCorners) els.outCorners.textContent = formatInt(res.corners);
        if (els.outBorders) els.outBorders.textContent = formatInt(res.borders);
        if (els.outCenters) els.outCenters.textContent = formatInt(res.centers);
        if (els.outGrid) {
          els.outGrid.textContent =
            formatArea(res.lengthFt, 0) + gridSuf + gridSep + formatArea(res.widthFt, 0) + gridSuf;
        }
      } finally {
        scheduleCollapsibleReflow(root);
      }
    }

    function onInput() {
      update();
    }

    if (els.unitFt) els.unitFt.addEventListener('click', function () { setUnit('ft'); });
    if (els.unitM) els.unitM.addEventListener('click', function () { setUnit('m'); });
    if (els.modeDim) els.modeDim.addEventListener('change', function () { if (els.modeDim.checked) setMode('dimensions'); });
    if (els.modeArea) els.modeArea.addEventListener('change', function () { if (els.modeArea.checked) setMode('area'); });

    ['input', 'change'].forEach(function (ev) {
      if (els.len) els.len.addEventListener(ev, onInput);
      if (els.wid) els.wid.addEventListener(ev, onInput);
      if (els.area) els.area.addEventListener(ev, onInput);
    });

    if (els.reset) {
      els.reset.addEventListener('click', function () {
        if (els.len) els.len.value = '';
        if (els.wid) els.wid.value = '';
        if (els.area) els.area.value = '';
        setMode('dimensions');
        setUnit('ft');
        update();
      });
    }

    [els.len, els.wid, els.area].forEach(function (el) {
      if (!el) return;
      el.addEventListener('focus', function () {
        root.classList.add('flooring-calculator--input-active');
      });
      el.addEventListener('blur', function () {
        root.classList.remove('flooring-calculator--input-active');
      });
    });

    setUnit('ft');
    setMode('dimensions');
    update();
  }

  function shouldBindCalculator(root) {
    if (!root) return false;
    if (root.closest('[data-accordion-blocks][hidden]')) return false;
    return true;
  }

  function initAll(scope) {
    var container = scope || document;
    container.querySelectorAll('[data-flooring-calculator]').forEach(function (root) {
      if (shouldBindCalculator(root)) {
        bind(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });

  document.addEventListener('quickview:loaded', function (evt) {
    var productId = evt.detail && evt.detail.productId;
    if (!productId) {
      initAll();
      return;
    }

    var modal = document.getElementById('QuickShopModal-' + productId);
    initAll(modal || document);
  });

  window.bindFlooringCalculator = bind;
})();
